import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Download, FileText, ChevronRight } from 'lucide-react';
import { MoldableIcon, BiocompatibleIcon, MechanicalIcon, ReconstructionIcon, AbsorptionIcon, RegenerationIcon, BioactiveIcon, NonToxicIcon, DefectSpanIcon, NonAnimalIcon, StorageIcon } from '../components/FlexiOssCinematic/PropertyIcons';
import { Button } from '../components/ui/button';
import SubpageLayout from '../components/FlexiOssCinematic/SubpageLayout';
import ReadAloud from '../components/ReadAloud';
import PhilHealthCallout from '../components/PhilHealthCallout';
import SocialShare from '../components/FlexiOssCinematic/SocialShare';
import StructuredData from '../components/StructuredData';
import { dentSchema } from '../data/productSchema';
import { HorizontalBorderStack, EditorialColumnSplit, GridGapDefinitionMatrix } from '../components/editorial/EditorialLayouts';
import { ProductPropertiesEditorial, ProductCertifications, ProductPublications } from '../components/product/ProductSections';
import ProductShareBar from '../components/product/ProductShareBar';
import { SITE_URL } from '../lib/siteConfig';

const DENT_SHARE_SUMMARY = [
  'FlexiOss® Dent — 3rd-generation synthetic bone graft (hydroxyapatite + curdlan polymer) for dental & maxillofacial surgery.',
  '',
  'KEY FACTS',
  '• PH-FDA certified (CMDR-2025-04943B); CE certified (EC 1434-MDD-269/2021).',
  '• 100% synthetic and animal-free — no prion or virus transmission risk.',
  '• Moldable and cut-to-size after ~60-second hydration; usable without a membrane; can be soaked in antibiotics, PRP or growth factors.',
  '• Available sizes: 1 cm, 3 cm and 5 cm (defects up to 7 cm).',
  '',
  'CLINICAL USES',
  '• Socket preservation, ridge augmentation, sinus lift, implant-site preparation, cyst/root-resection defect filling and oronasal fistula closure.',
].join('\n');

const DENT_CERTS = [
  { acr: 'CE', title: 'CE Certified', code: 'EC No. 1434-MDD-269/2021', desc: 'European conformity — highest safety standards' },
  { acr: 'FDA', title: 'PH-FDA Approved', code: 'CMDR-2025-04943B', desc: 'Certified for dental & maxillofacial applications in the Philippines' },
  { acr: 'PAT', title: 'Patent Protected', code: 'European Patents', desc: 'Protected across England, Austria, France, Spain, Ireland, Germany, Poland & Italy' },
];

const ACCENT = '#E91E8C';

const DENT_AWARDS = [
  { year: '2013', title: 'Best Invention by a Woman Inventor', body: 'World Intellectual Property Organization (WIPO / OMPI)' },
  { year: '2016', title: 'Prix Galien Poland, Innovative Scientific Discovery', body: 'Prix Galien Foundation' },
  { year: '2019', title: 'Economic Award of the President of Poland, Research & Development', body: 'Office of the President of the Republic of Poland' },
  { year: '2019', title: 'Future Product of the Entrepreneur', body: '21st Polish Future Product Competition' },
];

const API = process.env.REACT_APP_BACKEND_URL;
const dl = (url, name) => {
  const href = /^https?:\/\//i.test(url) && (!API || !url.startsWith(API))
    ? `${API}/api/download?url=${encodeURIComponent(url)}&name=${encodeURIComponent(name || 'FlexiOss.pdf')}`
    : url;
  const a = document.createElement('a');
  a.href = href; a.target = '_blank'; a.rel = 'noopener noreferrer';
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
};

const properties = [
  { icon: MoldableIcon, title: 'Flexible After Soaking', desc: 'Plastic and bendable — fits individual bone defects by cutting or bending' },
  { icon: BiocompatibleIcon, title: 'Bio-Compatible', desc: 'Compatible with natural bone tissue for seamless integration' },
  { icon: MechanicalIcon, title: 'Mechanical Parameters', desc: 'Similar to those of cancellous bone — structural support' },
  { icon: ReconstructionIcon, title: 'Supports Bone Reconstruction', desc: 'Actively supports the bone reconstruction process' },
  { icon: AbsorptionIcon, title: 'Absorbs Blood & Medicine', desc: 'High soaking capacity for enhanced drug delivery' },
  { icon: RegenerationIcon, title: 'Bone Tissue Regeneration', desc: 'Positively affects bone tissue regeneration process' },
  { icon: BioactiveIcon, title: 'Ionic Reactive / Bioactive', desc: 'High ionic reactivity increases mineralization rate' },
  { icon: NonToxicIcon, title: 'Non-Toxic Material', desc: 'Thoroughly tested for safety — no cytotoxicity' },
  { icon: DefectSpanIcon, title: 'Up to 7cm Defects', desc: 'Can be used for bone defects of up to 7 cm in length' },
  { icon: NonAnimalIcon, title: 'Non-Animal Material', desc: 'Lowers risk of pathogen transmission and allergic reactions' },
  { icon: StorageIcon, title: 'Easy to Store & Transport', desc: 'Simple storage and transportation requirements' },
];

const publications = [
  { title: 'Efficacy of a Novel Adaptable and Moldable Synthetic Polymer, in Open Socket Preservation Without a Membrane: Ease of Use and Volume Preservation in Alveolar Bone Regeneration', authors: 'Krasny K., Wróblewski S., Wojtowicz A., Kamiński A.', journal: 'Engineering of Biomaterials (Inżynieria Biomateriałów) 174, 2026', year: '2026', pdfUrl: 'http://www.biomaterials.pl/pdf-221194-139982?filename=Efficacy-of-a-novel-adapt.pdf', isNew: true },
  { title: 'Behavior of new hydroxyapatite/glucan composite in human serum', authors: 'Borkowski L., Lubek T., Jojczuk M., Nogalski A., Belcarz A., Palka K., Hajnos M., Ginalska G.', journal: 'J. Biomed. Mater. Res. Part B, 2018', year: '2018', pdfUrl: 'https://flexioss.pl/wp-content/uploads/2017/10/Journal_of_Biomedical_Materials_Research_Part_B__Applied_Biomaterials_2018.pdf' },
  { title: 'Unexpected reaction of new HAp/glucan composite to environmental acidification: Defect or advantage?', authors: 'Borkowski L., Kiernicka M., Belcarz A., Palka K., Hajnos M., Ginalska G.', journal: 'Biomed. Mater. Res. B Appl. Biomater. 2017', year: '2017', externalUrl: 'https://onlinelibrary.wiley.com/doi/abs/10.1002/jbm.b.33646' },
  { title: 'The comparison study of bioactivity between composites containing synthetic non-substituted and carbonate-substituted hydroxyapatite', authors: 'Borkowski L., Sroka-Bartnicka A., et al.', journal: 'Mater. Sci. Eng. C 2016, 62, 260-267', year: '2016', pdfUrl: 'https://flexioss.pl/wp-content/uploads/2021/03/The-comparison-study-of-bioactivity-between-composites-containing.pdf' },
  { title: 'Biphasic mode of antibacterial action of aminoglycoside antibiotics-loaded elastic hydroxyapatite-glucan composite', authors: 'Belcarz A., Zima A., Ginalska G.', journal: 'Int. J. Pharm. 2013, 454, 285-295', year: '2013', pdfUrl: 'https://flexioss.pl/wp-content/uploads/2020/10/EJP-2013.pdf' },
  { title: 'Application of β-1,3-glucan in production of ceramics-based elastic composite for bone repair', authors: 'Belcarz A., Ginalska G., Pycka T., et al.', journal: 'Centr. Eur. J. Biol. 2013, 8, 534-548', year: '2013', pdfUrl: 'https://flexioss.pl/wp-content/uploads/2021/03/Application-of-B-13-glucan-in-production-of.pdf' },
  { title: 'Efficacy of a Novel Adaptable and Moldable Synthetic Polymer, in Open Socket Preservation Without a Membrane (Original)', authors: 'Krasny K., Wroblewski S., Wojtowicz A., Kaminski A.', journal: 'Biomaterials', year: '2021', externalUrl: 'http://www.biomaterials.pl/pdf-221194-139982?filename=Efficacy-of-a-novel-adapt.pdf' },
];

const dentalApps = [
  'Post-extraction alveolar bone augmentation',
  'Dental implant site preparation and preservation',
  'Maxillofacial reconstructive procedures',
  'Treatment of gingival resection with bone atrophy',
  'Filling defects after cystotomy and root resection',
  'Oronasal fistulae treatment and closure',
  'Open socket preservation without membrane',
  'Bioactive filling of titanium scaffolds in implantology',
  'Treatment of dental cysts',
  'Interdental bone tissue regeneration',
  'Mastoid cavity obliteration following cholesteatoma surgery (tympanomastoidectomy)',
  'Temporal-bone / skull-base defect filling after cerebellopontine angle (CPA) tumor excision',
];

/* ── FlexiOss® Dent vs BonAlive® maxillofacial comparison ──────────────
   Sourced verbatim (condensed, no em dashes) from Medical Inventi S.A. and
   Bonalive Biomaterials Ltd. manufacturer documentation (BonAlive® Granules
   and Granules CMF IFU). FlexiOss® is the highlighted (pink) hero column. */
const BONALIVE_COLS = [
  { key: 'fo', name: 'FlexiOss® Dent', hero: true },
  { key: 'ba', name: 'BonAlive® Granules / CMF' },
];

const BONALIVE_ROWS = [
  { feat: 'Product form', fo: 'Pre-formed sterile cylinder in a double paper-foil pack, ready to place', ba: 'Bioactive glass granules in an individually packaged, pre-filled syringe' },
  { feat: 'Available sizes', fo: 'Ø13 mm cylinder in 1 cm, 3 cm and 5 cm lengths', ba: 'Granules: 1cc, 2.5cc, 5cc, 10cc. CMF: 2.5cc, 5cc' },
  { feat: 'Composition', fo: 'HAP granules (>100 nm) bound with curdlan, a β-1,3 glucan sugar polymer', ba: 'S53P4 bioactive glass: 53% SiO₂, 23% Na₂O, 20% CaO, 4% P₂O₅' },
  { feat: 'Porosity', fo: 'Micro and macroporous, ~30 µm pores, 68 to 70% porosity', ba: 'Solid granules. Porosity is limited to the inter-granular space plus a surface hydroxyapatite layer' },
  { feat: 'Handling & absorption', fo: 'Absorbs blood and plasma, becoming malleable and trimmable during surgery', ba: 'Does not absorb fluids. Moistened with saline before implantation. Volume and weight stay unchanged' },
  { feat: 'Intraoperative preparation', fo: 'Ready out of pack, no lengthy preparation', ba: 'Requires saline injection into the granule syringe and spatula assembly' },
  { feat: 'Intraoperative safety', fo: 'No loose granules to migrate, no glove-puncture risk', ba: 'Displaced granules can abrade nearby articular surfaces. Granules can puncture surgical gloves, a contamination risk' },
  { feat: 'Osteoconductivity', fo: 'Osteoconductive scaffold with strong osseointegration, supports vascularisation', ba: 'Osteoconductive and osteostimulating (non-osteoinductive)' },
  { feat: 'Resorbability', fo: 'HAP resorbs. Curdlan degrades to glucose that enters normal metabolism', ba: 'Forms a silica gel layer that crystallises to resorbable hydroxyapatite, replacing bone over several years' },
  { feat: 'Animal or human tissue', fo: 'None', ba: 'None' },
  { feat: 'Immunological / toxic reaction', fo: 'None', ba: 'Contraindicated in patients allergic to bioactive glass' },
  { feat: 'Sterilisation', fo: 'Ethylene oxide', ba: 'Irradiation' },
  { feat: 'X-ray visibility', fo: 'Yes', ba: 'Yes, also visible on CT and MRI' },
  { feat: 'Indications', fo: 'Small and large cavities across orthopedics, traumatology, dental and veterinary surgery', ba: 'Cranio-maxillofacial (mandible, frontal-sinus and mastoid obliteration, nasal-cavity narrowing). Also orthopedic cavity and osteomyelitis filling' },
];

const BONALIVE_ADVANTAGES = [
  { title: 'No lengthy preparation', text: 'FlexiOss® is ready to place straight from the pack. BonAlive® requires saline to be injected into the granule syringe and a spatula assembled before use.' },
  { title: 'No granule migration', text: 'FlexiOss® stays where it is placed. Loose BonAlive® granules can travel beyond the implanted area and abrade nearby articular surfaces, impeding movement.' },
  { title: 'No glove-puncture risk', text: 'The cohesive FlexiOss® cylinder carries no puncture hazard. BonAlive® granules can perforate surgical gloves, introducing a microbiological contamination risk.' },
  { title: 'Delivered EO-sterile', text: 'FlexiOss® is sterilised with ethylene oxide and supplied to the customer ready for the operating field.' },
];

const FlexiOssDentPage = () => {
  return (
    <SubpageLayout productName="Dent">
      <StructuredData id="dent-page" schema={dentSchema} />
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-b from-slate-900 to-slate-950 overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#E91E8C]/8 rounded-full blur-[120px]" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 rounded-full bg-[#E91E8C]/10 border border-[#E91E8C]/30 mb-6"><span className="text-[#E91E8C] font-bold text-sm">PH-FDA: CMDR-2025-04943B</span></div>
              <h1 className="text-4xl md:text-6xl font-black text-white mb-4 md:mb-6">FlexiOss<sup>®</sup> <span className="text-[#E91E8C]">Dent</span></h1>
              <p className="text-lg md:text-xl text-slate-300 mb-3 md:mb-4 leading-relaxed">Modern implantable biomaterial for dental and maxillofacial procedures. Available in 1cm, 3cm, and 5cm sizes.</p>
              <p className="text-sm text-slate-400 mb-6 md:mb-8 leading-relaxed">Third-generation biphasic (hydroxyapatite-polymeric) biomaterial, bioactive, supporting bone tissue regeneration. After implantation, it integrates with the patient's bone and is slowly remodelled into natural bone tissue — eliminating the need for reoperation.</p>
              <div className="mb-6"><ReadAloud accent={ACCENT} /></div>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                <Button onClick={() => dl(`${API}/api/document/dent-ifu`, 'FlexiOss_Dent_IFU.pdf')} className="bg-[#E91E8C] hover:bg-[#C2185B] text-white font-bold" data-testid="dent-dl-ifu"><Download className="w-4 h-4 mr-2" /> Download IFU</Button>
                <Button onClick={() => dl(`${API}/api/document/dent-catalogue`, 'FlexiOss_Dent_Catalogue.pdf')} variant="outline" className="border-[#E91E8C] text-[#E91E8C] hover:bg-[#E91E8C]/10 font-bold" data-testid="dent-dl-cat"><FileText className="w-4 h-4 mr-2" /> Product Catalogue</Button>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="relative group">
              <div className="absolute inset-0 bg-[#E91E8C] rounded-3xl blur-3xl" style={{ opacity: 0.10, animation: 'subtle-glow 8s ease-in-out infinite' }} />
              <style>{`@keyframes subtle-glow { 0%,100%{opacity:0.10} 50%{opacity:0.18} }`}</style>
              <img src="/flexioss-dent-transparent.png" alt="FlexiOss Dent — PH-FDA certified biphasic biomaterial for dental maxillofacial bone augmentation and socket preservation" className="relative w-full h-auto drop-shadow-2xl transition-transform duration-500 group-hover:scale-105" />
              {/* Size badges */}
              <div className="absolute bottom-4 right-4 flex gap-2">
                {['1cm', '3cm', '5cm'].map((s) => (<span key={s} className="bg-[#E91E8C] text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">{s}</span>))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Videos */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-4xl font-black text-white mb-8 text-center">Product <span className="text-[#E91E8C]">Demonstrations</span></h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {[
              { id: 'LhssUMR_L14', title: 'FlexiOss® Dent — Easy & Effective Bone Matrix', desc: 'Product overview showcasing the dental bone filler properties, flexibility, and clinical applications.' },
              { id: 'fbeJ6mRqutw', title: 'FlexiOss® — Clinical Application', desc: 'Clinical demonstration of FlexiOss® in dental procedures including socket preservation and bone augmentation.' },
            ].map((vid, i) => (
              <div key={i} className="bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-slate-700 rounded-2xl overflow-hidden hover:border-[#E91E8C] transition-all" data-testid={`dent-video-${i}`}>
                <div className="aspect-video"><iframe width="100%" height="100%" src={`https://www.youtube.com/embed/${vid.id}`} title={vid.title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" loading="lazy" /></div>
                <div className="p-5">
                  <h4 className="text-white font-bold text-base mb-1">{vid.title}</h4>
                  <p className="text-slate-400 text-sm leading-relaxed">{vid.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Properties */}
      <ProductPropertiesEditorial
        properties={properties}
        accent={ACCENT}
        testidPrefix="dent"
        bg="bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900"
        heading={<>What Makes <span className="text-[#E91E8C]">FlexiOss® Dent</span> Unique</>}
      />

      {/* What Makes It Unique — expanded features */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-4xl font-black text-white text-center mb-10">Surgical <span className="text-[#E91E8C]">Advantages</span></h2>
          <div className="max-w-5xl mx-auto">
            <GridGapDefinitionMatrix
              accent={ACCENT}
              columns={3}
              testid="dent-advantages"
              items={[
                { term: 'Surgical Handiness', def: 'Allows fitting during operation to the shape of individual bone defects by cutting or bending.' },
                { term: 'Can Be Milled', def: 'In dry state, FlexiOss® Dent can be milled for personalized composite implants.' },
                { term: 'Faster Ossification', def: 'High specific surface area and ionic reactivity increases mineralization rate and new bone formation.' },
                { term: 'Minimizes Contamination Risk', def: 'Curdlan polymer minimizes virus/prion risk. Forms collagen-like triple helix neutral to macrophages.' },
                { term: 'High Soaking Capacity', def: 'Can be soaked in antibacterial drugs, PRP, or growth factors, resembling expensive BMP-2-type substitutes.' },
                { term: 'Barrier Function', def: 'Helps prevent the migration of soft-tissue cells into the bone defect. Used standalone or combined with other grafting materials.' },
                { term: 'Easy Storage & Transport', def: 'No special conditions needed. Simple logistics for clinics worldwide.' },
              ]}
            />
          </div>
        </div>
      </section>

      {/* Maxillofacial comparison — FlexiOss® Dent vs BonAlive® (bespoke hairline matrix) */}
      <section className="border-t border-white/10 bg-slate-950 py-20" data-testid="dent-bonalive">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mb-10 max-w-3xl">
            <p className="font-display text-[11px] font-bold uppercase tracking-[0.32em] text-[#E91E8C]">
              Maxillofacial Applications
            </p>
            <h2 className="mt-4 font-serif text-3xl italic leading-[1.08] tracking-tight text-white md:text-5xl">
              FlexiOss<sup className="align-super text-lg not-italic">®</sup> Dent vs BonAlive<sup className="align-super text-lg not-italic">®</sup>
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-slate-400 sm:text-base">
              A side-by-side against S53P4 bioactive glass granules for cranio-maxillofacial reconstruction. Every figure is
              drawn from each manufacturer's own published documentation.
            </p>
          </div>

          <div className="-mx-6 overflow-x-auto px-6 pb-2 sm:mx-0 sm:px-0" data-testid="dent-bonalive-scroll">
            <table className="w-full min-w-[720px] border-separate border-spacing-px bg-white/10 text-left">
              <caption className="sr-only">FlexiOss Dent compared with BonAlive Granules for maxillofacial applications</caption>
              <thead>
                <tr>
                  <th scope="col" className="bg-slate-950 px-5 py-4 align-bottom text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
                    Attribute
                  </th>
                  {BONALIVE_COLS.map((c) => (
                    <th
                      key={c.key}
                      scope="col"
                      className={`px-5 py-4 align-bottom ${c.hero ? 'bg-[#E91E8C]' : 'bg-slate-950'}`}
                    >
                      <span className={`block font-display text-sm font-bold uppercase tracking-[0.08em] ${c.hero ? 'text-white' : 'text-slate-300'}`}>
                        {c.name}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {BONALIVE_ROWS.map((row, ri) => (
                  <motion.tr
                    key={row.feat}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.4, delay: Math.min(ri * 0.03, 0.3) }}
                  >
                    <th scope="row" className="bg-slate-900 px-5 py-4 align-top text-sm font-semibold text-white">
                      {row.feat}
                    </th>
                    {BONALIVE_COLS.map((c) => (
                      <td
                        key={c.key}
                        className={`px-5 py-4 align-top text-sm leading-relaxed ${c.hero ? 'bg-[#E91E8C]/[0.07] text-white' : 'bg-slate-900 text-slate-300'}`}
                      >
                        {c.hero && (
                          <span aria-hidden="true" className="mr-2 inline-block h-1.5 w-1.5 -translate-y-0.5 bg-[#E91E8C] align-middle" />
                        )}
                        {row[c.key]}
                      </td>
                    ))}
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-5 text-xs leading-relaxed text-slate-500">
            Comparison drawn from Medical Inventi S.A. and Bonalive Biomaterials Ltd. manufacturer documentation
            (BonAlive<sup>®</sup> Granules and Granules CMF Instructions For Use). Provided for clinical reference.
          </p>

          {/* FlexiOss advantages — indexed editorial blocks (mono index, hairline separators) */}
          <div className="mt-14 grid gap-px border border-white/10 bg-white/10 md:grid-cols-2">
            {BONALIVE_ADVANTAGES.map((a, i) => (
              <motion.div
                key={a.title}
                data-testid={`dent-bonalive-advantage-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="bg-slate-950 p-8"
              >
                <div className="flex items-baseline gap-4">
                  <span className="font-mono text-sm tracking-[0.2em] text-[#E91E8C]">{String(i + 1).padStart(2, '0')}</span>
                  <h3 className="font-display text-lg font-bold tracking-tight text-white">{a.title}</h3>
                </div>
                <p className="mt-4 pl-9 text-sm leading-relaxed text-slate-400">{a.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PhilHealth Reimbursement */}
      <section className="bg-slate-900 px-6 lg:px-8 pt-4"><div className="mx-auto max-w-5xl"><PhilHealthCallout /></div></section>

      {/* Composition + Applications */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <div className="inline-block px-6 py-2 rounded-full bg-[#E91E8C]/10 border border-[#E91E8C]/30 mb-4"><span className="text-[#E91E8C] font-bold text-sm tracking-wider">TRICUREAN COMPOSITION</span></div>
              <h2 className="text-3xl font-black text-white mb-6">Biphasic Formula</h2>
              <p className="text-slate-300 leading-relaxed mb-8">Biphasic (hydroxyapatite-polymeric) composite obtained by polymer gelling into an irreversible, collagen-like triple helix — neutral for macrophages, no local inflammation.</p>
              <EditorialColumnSplit
                accent={ACCENT}
                testid="dent-composition"
                items={[
                  { term: 'Hydroxyapatite (HAP)', role: 'Ceramic Phase', def: <HorizontalBorderStack numbered={false} dense accent={ACCENT} testid="dent-hap" items={['Low-sintered, highly porous ceramic', 'Fully synthetic — high chemical stability', 'Active bone regeneration via ion uptake']} /> },
                  { term: 'Curdlan Polymer', role: 'Polymeric Phase', def: <HorizontalBorderStack numbered={false} dense accent={ACCENT} testid="dent-curdlan" items={['Triple-helix (collagen-like) structure', 'Non-inflammatory conformation', 'Enables flexibility & high absorbency']} /> },
                ]}
              />
            </div>
            <div>
              <div className="inline-block px-6 py-2 rounded-full bg-[#E91E8C]/10 border border-[#E91E8C]/30 mb-4"><span className="text-[#E91E8C] font-bold text-sm tracking-wider">DENTAL APPLICATIONS</span></div>
              <h2 className="text-3xl font-black text-white mb-6">Clinical Uses</h2>
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-slate-700 rounded-2xl p-6 sm:p-8">
                <HorizontalBorderStack items={dentalApps} accent={ACCENT} testid="dent-applications" />
              </div>
              <div className="mt-6 bg-slate-800/60 border border-slate-700 rounded-xl p-5">
                <h4 className="text-white font-bold mb-2">Available Sizes</h4>
                <div className="flex gap-3">{['1 cm (small defects)', '3 cm (medium defects)', '5 cm (large defects)'].map((s, i) => (<span key={i} className="bg-[#E91E8C]/10 border border-[#E91E8C]/30 text-[#E91E8C] text-xs font-bold px-3 py-1.5 rounded-full">{s}</span>))}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <ProductCertifications testidPrefix="dent" accent={ACCENT} items={DENT_CERTS} bg="bg-slate-950" />

      {/* Awards & Recognition */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="inline-block px-6 py-2 rounded-full bg-[#E91E8C]/10 border border-[#E91E8C]/30 mb-4"><span className="text-[#E91E8C] font-bold text-sm tracking-wider">AWARDS & RECOGNITION</span></div>
          <h2 className="text-3xl md:text-4xl font-black text-white mb-3">International Recognition</h2>
          <p className="text-slate-400 max-w-prose mb-8 leading-relaxed">The FlexiOss® technology and its inventors have been recognised by leading scientific and intellectual-property institutions.</p>
          <div className="border-t border-slate-800" data-testid="dent-awards">
            {DENT_AWARDS.map((a, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="grid grid-cols-[3.5rem_1fr] gap-4 border-b border-slate-800 py-5 sm:grid-cols-[4rem_1fr] sm:gap-8"
                data-testid={`dent-award-${i}`}
              >
                <span className="font-mono text-sm font-bold pt-0.5" style={{ color: ACCENT }}>{a.year}</span>
                <div>
                  <h3 className="text-white font-bold text-base leading-snug sm:text-lg">{a.title}</h3>
                  <p className="text-slate-500 text-sm mt-1">{a.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="text-slate-500 text-xs mt-6 max-w-prose leading-relaxed">Developed and manufactured by Medical Inventi S.A. (Poland). Exclusively distributed in the Philippines by Fortschritt Medical Supplies Inc.</p>
        </div>
      </section>

      {/* Clinical Studies */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-block px-6 py-2 rounded-full bg-[#E91E8C]/10 border border-[#E91E8C]/30 mb-4"><span className="text-[#E91E8C] font-bold text-sm tracking-wider">CLINICAL EVIDENCE</span></div>
            <h2 className="text-4xl font-black text-white">Documented <span className="text-[#E91E8C]">Results</span></h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {[
              { title: 'Oronasal Fistula Treatment', desc: 'Cat oronasal fistula filling procedure with X-ray evaluation directly after implantation and 1 month post-op showing positive integration.' },
              { title: 'Canine Oronasal Fistula', desc: 'Dog oronasal fistula filling — removal of luxated canine tooth and filling of the fistula with FlexiOss® Dent biomaterial.' },
              { title: 'Bone Density Progression', desc: 'MicroCT (pQCT) evaluation showing bone density increase from implantation through 1, 3, and 6 months — confirming progressive bone formation.' },
              { title: 'Histological Integration', desc: 'Histological images confirming bone cells, laminae, collagen fibres and Haversian canals at implantation site — unequivocal bone tissue formation.' },
              { title: 'Gingival Resection Treatment', desc: 'Treatment of gingival resection with serious atrophy of interdental gingiva and bone tissue between 21st and 22nd teeth — successful regeneration.' },
              { title: 'Post-Cystotomy Filling', desc: 'Successful filling of dental defect after cystotomy procedure — demonstrating remodelling and formation of bone tissue with no side effects.' },
            ].map((cs, i) => (
              <div key={i} className="bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-slate-700 rounded-xl p-6 hover:border-[#E91E8C] transition-all" data-testid={`dent-case-${i}`}>
                <h3 className="text-white font-bold mb-2">{cs.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{cs.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-slate-500 text-xs mt-6">Clinical observations conducted at New-Dent Stomatological Center, Lublin, Poland and Animal Surgery Clinic, University of Life Sciences, Lublin</p>
        </div>
      </section>

      {/* Publications */}
      <ProductPublications publications={publications} dl={dl} accent={ACCENT} testidPrefix="dent" bg="bg-slate-950" />

      {/* CTA */}
      <section className="py-20 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-black text-white mb-6">Ready to Experience FlexiOss<sup>®</sup> <span className="text-[#E91E8C]">Dent</span>?</h2>
          <p className="text-xl text-slate-300 mb-8">Contact our sales team for product information, pricing, and ordering.</p>
          <Link to="/"><Button size="lg" className="bg-[#E91E8C] hover:bg-[#C2185B] text-white font-bold px-12 py-6 text-xl shadow-2xl shadow-[#E91E8C]/50 hover:scale-105 transition-all" data-testid="dent-cta">Request for Samples <ChevronRight className="ml-2 w-5 h-5" /></Button></Link>
          <div className="mt-10 flex justify-center">
            <ProductShareBar title="FlexiOss® Dent" subject="FlexiOss® Dent — synthetic bone graft for dental & maxillofacial surgery" summary={DENT_SHARE_SUMMARY} pageUrl={`${SITE_URL}/flexioss-dent`} accent={ACCENT} testidPrefix="dent" />
          </div>
          <div className="mt-10"><SocialShare pageKey="dent" accent={ACCENT} /></div>
        </div>
      </section>

    </SubpageLayout>
  );
};

export default FlexiOssDentPage;
